import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { IWebRequestOptions } from '../../interfaces/web-request.interface';
import {
  BioCatchConfigModel,
  CityModel,
} from '../../models/abre-cuentas.model';
import { ConductRequestBodyModel } from '../../models/conducts-request-body.model';
import { DocumentModel } from '../../models/document.model';
import { LogDeviceModel } from '../../models/log-device.model';
import { StringFormatPipe } from '../../pipe/string.format.pipe';
import { AbreCuentasService } from '../abre-cuentas/abre-cuentas.service';
import { PlatformEndpointService } from '../communicator/endpoint/endpoint.service';
import { PlatformCommunicatorAuthService } from '../communicator/platform-communicator/platform-communicator.auth.service';
import { PlatformCommunicatorService } from '../communicator/platform-communicator/platform-communicator-onboarding.service';
import { ValidationHelperService } from '../helpers/validation-helper.service';
import { ParametersService } from '../parameters/parameters.service';
import { SpecialOfferModel } from '../../models/specialOffer/special-offer.model';
import { ChannelCommunicatorService } from '../communicator/channel-communicator/channel-communicator.service';
import { CHANNEL } from '../../constants/channel.constants';
import { CryptoVaultService } from '../external/crypto-vault/crypto-vault.service';

/* para esos service agregar el crypto en true cuando se realice ajuste en cryptovault
  URLS.SEND_BACK_DOCUMENT_INFO ||
   URLS.FRONT_DOCUMENT_INFO ||
   URLS.FINGER_PRINTS_INFO ||
   URLS.AGREEMENTS_OFFERS) {
   headers = headers.append('use_cripto', 'false');
}*/
const headerCrypto = new HttpHeaders({
  use_cripto: 'true',
});
/**
 * Obtener el valor de una dependencia, en este caso, ésta clase es visible para toda la app
 *
 * *Obtain the valua of a dependency, in this case, this class is visible for the entire app*
 */
@Injectable({
  providedIn: 'root',
})

/**
 * Clase de solicitudes con el servidor
 *
 * communication class with the server
 */
export class RequestService {
  /**
   * Summary Constructor of the component.
   *
   * @param {PlatformEndpointService}     endpointService      Injectable endpointService service.
   * @param {PlatformCommunicatorService} communicatorService  Injectable communicatorService service.
   * @param {StringFormatPipe}            stringFormat         Injectable stringFormat pipe.
   */
  constructor(
    private endpointService: PlatformEndpointService,
    private communicatorService: PlatformCommunicatorService,
    private channelCommunicatorService: ChannelCommunicatorService,
    private stringFormat: StringFormatPipe,
    private authService: PlatformCommunicatorAuthService,
    private parametersService: ParametersService,
    private validationHelperService: ValidationHelperService,
    private httpClient: HttpClient,
    private cipherService: CryptoVaultService,
    private abreCuentasService: AbreCuentasService,
  ) { }

  // variables

  private ratesList: any;

  /**
   * Obtiene la calificación de acuerdo a los parámetros enviados por el usuario
   *
   * *Gets the score depending on users parameteres that were sent*
   *
   * @param config
   * @param bodyFields
   * @param csid
   * @param activity
   * @param brand
   * @param ppid
   * @param solution
   * @param deviceModel
   * @param ip
   * @param applicationID
   * @returns {Promise}
   * @method get
   */
  async getScore(
    config: BioCatchConfigModel,
    bodyFields: any,
    csid: string,
    activity: string,
    brand: string,
    ppid: string,
    solution: string,
    deviceModel: string,
    ip?: string,
    applicationID?: string,
    yearOfBrith?: string,
  ): Promise<any> {
    const data = new ConductRequestBodyModel(
      bodyFields,
      config.action,
      activity,
      brand,
      ppid,
      solution,
      deviceModel,
      ip,
    );

    if (applicationID !== undefined) {
      data.applicationId = applicationID;
    }

    if (
      yearOfBrith &&
      activity === bodyFields.activity_type_pre &&
      bodyFields.application_type === 'WEB'
    ) {
      data.yearOfBirth = yearOfBrith;
      data.activityName = bodyFields.activity_name;
    }

    const headers = new HttpHeaders({
      application: bodyFields.application,
      session_id: csid,
    });

    const options = {
      url: this.endpointService.URLS.BIOCATCH_UPDATE,
      method: 'post',
      body: {
        data,
      },
      headers,
    };
    const appendSessionHeader = csid === 'null' ? true : false;
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false, true, appendSessionHeader)
        .then(response => resolve(response.body.data))
        .catch(err => reject(err));
    });
  }

  /**
   *
   * Envía los parámetros para revisar la calificación del usuario
   *
   * *Sends parameters to check user score*
   *
   * @param any body
   * @returns {Promise}
   * @method post
   */
  async checkScore(body: any) {
    const options = {
      url: this.endpointService.URLS.BIOCATCH_CHECK_SCORE,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true)
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Guarda la información del dispositivo en la bítacora
   *
   * *Save device information in Bitacora*
   *
   * @param {LogDeviceModel} device
   * @returns {Promise}
   * @method post
   */
  async saveDataInBitacora(
    device: LogDeviceModel,
    isCSID = false,
    crypto = false,
  ): Promise<any> {
    if (isCSID) {
      device.device.device_id = 'csid';
    }
    // eslint-disable-line nombre-del-mensaje
    const headers = new HttpHeaders({
      'token-client-id': '39540626_1',
      'session_id': this.authService.getSessionId(),
    });
    const options = {
      url: this.endpointService.URLS.TERMS_CONDITIONS,
      method: 'post',
      body: device,
      headers,
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, crypto)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Valida las reglas
   *
   * *Rules Validation*TERMS_CONDITIONS
   *
   * @param {any} rules
   * @returns {Promise}
   * @method post
   */
  async validateRules(rules: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.VALIDATE_RULES,
      method: 'post',
      body: rules,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Asigna el tipo de producto
   *
   * *Assign product type*
   *
   * @param {string} product
   * @returns {Promise}
   * @method post
   */
  async assignProductType(product: string): Promise<any> {
    const options = {
      url: this.endpointService.URLS.ASSIGN_PRODUCT_TYPE,
      method: 'patch',
      body: {
        product_type: product,
      },
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Asigna el tipo de producto
   *
   * *Assign product type*
   *
   * @param {string} product
   * @returns {Promise}
   * @method post
   */
  async offersSelectAgreement(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.OFFERS_SELECT_AGREEMENT,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => {
          resolve(response);
        })
        .catch(err => reject(err));
    });
  }

  /**
   * Retorna los acuerdos de cada oferta
   *
   * *Gets Offer's Agreements*
   *
   * @method get
   * @returns {Promise}
   */
  async getAgreementsOffers(): Promise<any> {
    const options = {
      url: this.endpointService.URLS.AGREEMENTS_OFFERS,
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Retorna los datos ingresados en el simulador
   *
   * *Gets Simulator Data*
   *
   * @method get
   * @returns {Promise}
   */
  async getSimulationData(): Promise<any> {
    const options = {
      url: this.endpointService.URLS.GET_SIMULATION,
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }
  /**
   * Retorna los datos de PCO
   *
   * *Gets Channel Notification Data*
   *
   * @method get
   * @returns {Promise}
   */
  async getChannelNotification(solicitationId: string): Promise<any> {
    const url = this.endpointService.URLS.GET_CHANNEL_NOTIFICATION;
    const document_number =
      this.abreCuentasService.getDocumentInfo().document_number;
    const document_type =
      this.abreCuentasService.getDocumentInfo().document_type;
    const headers = new HttpHeaders({
      'token-client-id': document_number + '_' + document_type,
    });
    const options = {
      url: this.stringFormat.transform(url, [solicitationId]),
      method: 'get',
      headers,
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }
  /**
   * Retorna los datos de PCO para un cliente (Solicitudes)
   *
   * *Gets Channel Notification Data*
   *
   * @method get
   * @returns {Promise}
   */
  async getChannelNotificationClient(
    token_client_id?: string,
    consulta = false,
  ): Promise<any> {
    const url = this.endpointService.URLS.GET_CHANNEL_NOTIFICATION_2;
    let headers;
    if (!consulta) {
      headers = token_client_id
        ? new HttpHeaders({
          'token-client-id': token_client_id,
        })
        : undefined;
      console.log(token_client_id, 'token_client_id');
    }
    const options = {
      url,
      method: 'get',
      headers,
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  async formatDocumentInfo(documentInfo: any) {
    console.log(documentInfo);
    await this.cipherService.refreshKeyCrypto();
    let document_type = documentInfo.document_type === '' ? null : documentInfo.document_type;
    let card_number = documentInfo.card_number === '' ? null : documentInfo.card_number;
    let document_number = documentInfo.document_number === '' ? null : documentInfo.document_number;
    let primary_lastname = documentInfo.primary_lastname === '' ? null : documentInfo.primary_lastname;
    let second_lastname = documentInfo.second_lastname === '' ? null : documentInfo.second_lastname;
    let firstname = documentInfo.firstname === '' ? null : documentInfo.firstname;
    let middlename = documentInfo.middlename === '' ? null : documentInfo.middlename;
    let sex = documentInfo.sex === '' ? null : documentInfo.sex;
    let birthdate = documentInfo.birthdate === '' ? null : documentInfo.birthdate;

    if (document_type) {
      document_type = await this.cipherService
        .encrypt(document_type);
    }
    if (card_number) {
      card_number = await this.cipherService
        .encrypt(card_number);
    }
    if (document_number) {
      document_number = await this.cipherService
        .encrypt(document_number);
    }
    if (primary_lastname) {
      primary_lastname = await this.cipherService
        .encrypt(primary_lastname);
    }
    if (second_lastname != null) {
      second_lastname = await this.cipherService
        .encrypt(second_lastname);
    }
    if (firstname) {
      firstname = await this.cipherService
        .encrypt(firstname);
    }
    if (middlename != null) {
      middlename = await this.cipherService
        .encrypt(middlename);
    }
    if (sex) {
      sex = await this.cipherService.encrypt(sex);
    }
    if (birthdate) {
      birthdate = await this.cipherService
        .encrypt(birthdate);
    }

    // eslint-disable-line nombre-del-mensaje
    return {
      ...documentInfo,
      birthdate,
      document_type,
      card_number,
      document_number,
      primary_lastname,
      second_lastname,
      firstname,
      middlename,
      sex,
    };
  }

  /**
   * Envía la información de la parte de atrás del documento
   *
   * *Sends back document info*
   *
   * @method post
   * @returns {Promise}
   */
  async sendBackDocumentInfo(documentInfo: DocumentModel): Promise<any> {
    const body = await this.formatDocumentInfo(documentInfo);
    const options = {
      url: this.endpointService.URLS.SEND_BACK_DOCUMENT_INFO,
      method: 'post',
      body,
      headerCrypto,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => {
          resolve(response);
        })
        .catch(err => (err.status === 422 ? resolve(err) : reject(err)));
    });
  }

  /**
   * Envía la información de la parte frontal del documento
   *
   * *Sends front document info*
   *
   * @method post
   * @returns {Promise}
   */
  async sendFrontDocumentInfo(
    documentPhoto: string,
    facePhoto: string,
  ): Promise<any> {
    const options = {
      url: this.endpointService.URLS.FRONT_DOCUMENT_INFO,
      method: 'post',
      body: {
        front_document_picture: documentPhoto,
        face_picture: facePhoto,
      },
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Envia la información de las huellas
   *
   * *Sends finger prints information*
   * @method post
   * @returns {Promise}
   */
  async sendFingerPrintsInfo(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.FINGER_PRINTS_INFO,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Obtiene la información de un cliente
   *
   * *Gets client information*
   * @method get
   * @returns {Promise}
   */
  async getClientInformation(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.CLIENT_INFORMATION,
      method: 'post',
      body,
    };
    console.warn('consumiendo getClientInformation----');
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true)
        .then(response => {
          console.log('Rta exitosa: ', response);
          resolve(response);
        })
        .catch(err => {
          console.warn('Error: ', err);
          reject(err);
        });
    });
  }

  /**
   * Obtiene la información de un cliente
   *
   * *Gets client information*
   * @method get
   * @returns {Promise}
   */
  async getCardCategoryByBrand(body): Promise<any> {
    const headers = new HttpHeaders({
      username: 'FP446215',
      // use_cripto: 'false',
    });
    const options = {
      url: this.endpointService.URLS.GET_CARD_CATEGORY_BY_BRAND,
      method: 'post',
      body,
      headers,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true)
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Obtiene la información de un cliente
   *
   * *Gets client information*
   * @method get
   * @returns {Promise}
   */
  async getResultPco(body): Promise<any> {
    const options = {
      url: this.endpointService.URLS.GET_RESULT_PCO,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        // .then(response => resolve(response.body))
        .then(response => {
          resolve(response);
        })
        .catch(err => reject(err));
    });
  }

  /**
   * Obtiene la publicacion de firma
   *
   * **
   * @method getc
   * @returns {Promise}
   */
  async getOTP(body): Promise<any> {
    const options = {
      url: this.endpointService.URLS.GET_OTP,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true)
        .then(response => resolve(response.body))
        .catch(err => (err.status === 422 ? resolve(err) : reject(err)));
    });
  }

  /**
   * Valida el codigo otp
   *
   * **
   * @method get
   * @returns {Promise}
   */
  async validateOTP(body): Promise<any> {
    const options = {
      url: this.endpointService.URLS.VALIDATE_OTP,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true)
        .then(response => resolve(response))
        .catch(err => (err.status === 422 ? resolve(err) : reject(err)));
    });
  }
  /**
   * Envia la ocupación
   *
   * *Sends prospect occupation*
   * @method post
   * @returns {Promise}
   */
  async getSessionId(): Promise<any> {
    const options = {
      url: this.endpointService.URLS.GET_SESSION_ID,
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => {
          resolve(response);
        })
        .catch(err => reject(err));
    });
  }
  /**
   * Envia la ocupación
   *
   * *Sends prospect occupation*
   * @method post
   * @returns {Promise}
   */
  async sendProspectOccupation(body: any, parms: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_OCCUPATION,
      method: 'post',
      body,
      params: parms,
    };
    console.log('body ------>', body);
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => {
          resolve(response);
        })
        .catch(err => reject(err));
    });
  }

  /**
   * Envia la ocupación independiente
   *
   * *Sends prospect independent occupation*
   * @method post
   * @returns {Promise}
   */
  async sendProspectIndependentOccupation(occupation: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_INDEPENDENT_OCCUPATION,
      method: 'post',
      body: occupation,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response))
        // .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Envia la ocupación independiente
   *
   * *Sends prospect independent occupation*
   * @method post
   * @returns {Promise}
   */
  async sendPostTermsAndConditions(devicePrint: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_INDEPENDENT_OCCUPATION,
      method: 'post',
      devicePrint,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Envia la ocupación independiente
   *
   * *Sends prospect independent occupation*
   * @method post
   * @returns {Promise}
   */
  async sendProspectIncomeForm(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_INCOME_FORM,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Envia la ocupación independiente
   *
   * *Sends prospect independent occupation*
   * @method post
   * @returns {Promise}
   */
  async sendProspectOperationCurrency(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_OPERATION_CURRENCY,
      method: 'patch',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Envia la ocupación independiente
   *
   * *Sends prospect independent occupation*
   * @method post
   * @returns {Promise}
   */
  async sendProspectForeignCurrencyProducts(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_FOREIGN_PRODUCT_DETAIL,
      method: 'patch',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Envia la ciudad de nacimiento
   *
   * *Sends prospect birthplace*
   * @method post
   * @returns {Promise}
   */
  async sendProspectBirthplace(body: CityModel): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_BIRTHPLACE,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Crea un caso de información de dirección
   *
   * *Creates a address information case*
   * @method post
   * @returns {Promise}
   */
  async createCaseAddress(body: any, solicitationId: string): Promise<any> {
    const isPassive = this.validationHelperService.isPassiveProduct();
    const options = {
      url: this.stringFormat.transform(
        isPassive
          ? this.endpointService.URLS.PROSPECT_CREATE_CASE_ADDRESS
          : this.endpointService.URLS.PROSPECT_CREATE_CASE_ADDRESS_ACTIVO,
        [solicitationId],
      ),
      method: isPassive ? 'patch' : 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Crea un caso de información de contacto
   *
   * *Creates a contact information case*
   * @method post
   * @returns {Promise}
   */
  async createCaseContactInfo(body: any, solicitationId: string): Promise<any> {
    const isPassive = this.validationHelperService.isPassiveProduct();
    const url = isPassive
      ? this.endpointService.URLS.CONTACT_INFO
      : this.endpointService.URLS.CONTACT_INFO_ACTIVOS;
    const options = {
      url: this.stringFormat.transform(url, [solicitationId]),
      method: isPassive ? 'patch' : 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => {
          if (this.errCrypto(err.message)) {
            this.saveBitacora('ups_error_700_crypto_contact');
          }
          reject(err);
        });
    });
  }

  /**
   * Crea un nuevo cliente.
   *
   * *Creates a new client*
   * @method post
   * @returns {Promise}
   */
  async createClient(body: any, solicitationId: string): Promise<any> {
    const isPassive = this.validationHelperService.isPassiveProduct();
    const options = {
      url: this.stringFormat.transform(
        isPassive
          ? this.endpointService.URLS.CREATE_CLIENT
          : this.endpointService.URLS.CREATE_CLIENT_ACTIVOS,
        [solicitationId],
      ),
      method: isPassive ? 'patch' : 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => {
          if (this.errCrypto(err.message)) {
            this.saveBitacora('ups_error_700_crypto_clients');
          }
          reject(err);
        });
    });
  }

  /**
   * Registra un nuevo producto al cliente.
   *
   * *Register a new product for the customer*
   * @method post
   * @returns {Promise}
   */
  async createProduct(body: any, solicitationId: string): Promise<any> {
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.CREATE_PRODUCT,
        [solicitationId],
      ),
      method: 'patch',
      body,
      // headerCrypto,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Ejecuta la solicitud del cliente cliente.
   *
   * *Excecute de customer request*
   * @method post
   * @returns {Promise}
   */
  async performSolicitation(body: any, solicitationId: string): Promise<any> {
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.CREATE_CASE_PERFORM_SOLICITATION,
        [solicitationId],
      ),
      method: 'patch',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Ejecuta la solicitud del cliente cliente.
   *
   * *Excecute de customer request*
   * @method post
   * @returns {Promise}
   */
  async getCase(solicitationId: string, productCode: string): Promise<any> {
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.GET_CASE_SOLICITATION_INFO,
        [solicitationId],
      ),
      method: 'get',
      params: {
        productType: productCode,
      },
      // headerCrypto,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Envia la informacion de contacto de un prospecto
   *
   * *Sends prospect contact*
   * @method post
   * @returns {Promise}
   */
  async sendProspectContact(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_CONTACT,
      method: 'post',
      body,
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Función guardar información de la ciudad y dirección de la oficina
   *
   * *Function that saves office address*
   *
   * @method post
   * @returns {Promise}
   */
  async sendProspectOfficeAddress(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_OFFICE_ADDRESS,
      method: 'post',
      body,
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Función guardar información financiera
   *
   * *Function that saves financial info*
   *
   * @method post
   * @returns {Promise}
   */
  async sendProspectFinancial(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.PROSPECT_FINANCIAL,
      method: 'patch',
      body,
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => {
          resolve(response);
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  /**
   * Crea un caso de solicitud
   *
   * *Creates a solicitation case*
   * @method post
   * @returns {Promise}
   */
  // Servicio en mock
  async createCaseSolicitationId(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.GET_CREATE_CASE_SOLICITATION_ID,
      method: 'post',
      body,
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(
          response => {
            resolve(response.body.solicitation_id);
          },
          err => reject(err),
        )
        .catch(err => reject(err));
    });
  }

  /**
   * Crea un caso de información de contacto
   *
   * *Creates a contact information case*
   * @method post
   * @returns {Promise}
   */
  async createCaseFinancials(body: any, solicitationId: string): Promise<any> {
    const isPassive = this.validationHelperService.isPassiveProduct();
    const options = {
      url: this.stringFormat.transform(
        isPassive
          ? this.endpointService.URLS.PROSPECT_CREATE_CASE_FINANCIAL
          : this.endpointService.URLS.PROSPECT_CREATE_CASE_FINANCIAL_ACTIVOS,
        [solicitationId],
      ),
      method: isPassive ? 'patch' : 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => {
          resolve(response.body);
        })
        .catch(err => {
          if (this.errCrypto(err.message)) {
            this.saveBitacora('ups_error_700_crypto_financial');
          }
          reject(err);
        });
    });
  }

  /**
   * Crea un caso para actualizar la configuración de la cuenta
   *
   * *Update case account config case*
   * @method post
   * @returns {Promise}
   */
  async updateCaseAccountConfig(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.UPDATE_CASE_ACCOUNT_CONFIG,
      method: 'patch',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Crea un caso de información de contacto
   *
   * *Creates a contact information case*
   * @method post
   * @returns {Promise}
   */
  async foreignOperationsCase(body: any, solicitationId: string): Promise<any> {
    const isPassive = this.validationHelperService.isPassiveProduct();
    const options = {
      url: this.stringFormat.transform(
        isPassive
          ? this.endpointService.URLS.PROSPECT_CREATE_CASE_FOREIGN_OPERATION
          : this.endpointService.URLS.PROSPECT_CREATE_CASE_FOREIGN_ACTIVOS,
        [solicitationId],
      ),
      method: isPassive ? 'patch' : 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => {
          if (this.errCrypto(err.message)) {
            this.saveBitacora('ups_error_700_crypto_operations');
          }
          reject(err);
        });
    });
  }

  /**
   * Función asíncrona para enviar la información de configuración
   *
   * *Async Function that sends config*
   * @method post
   * @returns {Promise}
   */
  async offersConfig(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.OFFERS_CONFIG,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Función asíncrona para enviar la información de beneficios
   *
   * *Async Function that get benefits*
   * @method post
   * @returns {Promise}
   */
  async offersGetBenefits(agreementCode: any): Promise<any> {
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.OFFERS_GET_BENEFITS,
        [agreementCode],
      ),
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Función asíncrona para crear el caso de solicitud
   *
   * *Async Function that create case perform*
   * @method post
   * @returns {Promise}
   */
  async createCasePerformSolicitation(
    body: any,
    solicitationId: string,
  ): Promise<any> {
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.CREATE_CASE_PERFORM_SOLICITATION,
        [solicitationId],
      ),
      method: 'patch',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Función asíncrona para obtener información del caso
   *
   * *Async Function that get case info*
   * @method get
   * @returns {Promise}
   */
  async getCaseSolicitationInfo(
    solicitationId: string,
    productCode: string,
  ): Promise<any> {
    const headers = new HttpHeaders({
      channel_id: '141',
    });
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.GET_CASE_SOLICITATION_INFO,
        [solicitationId],
      ),
      method: 'get',
      params: {
        productType: productCode,
      },
      headers,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true)
        // .then(response => resolve(response.body))
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * envia informacion de direcciones
   *
   * *send address informatino*
   * @method post
   * @returns {Promise}
   */
  async getProspectAddress(body: any, solicitationId: string): Promise<any> {
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.CREATE_CLIENT,
        [solicitationId],
      ),
      method: 'patch',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => {
          if (this.errCrypto(err.message)) {
            this.saveBitacora('ups_error_700_crypto_address');
          }
          reject(err);
        });
    });
  }

  async getRatesFromServer(): Promise<any> {
    const options = {
      url: this.endpointService.URLS.GET_RATES,
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Trae los productos del Aprobado En Firme que se cargan a través de vitrinas
   * @param {String} offer_id - Id de la oferta que se mostró en la vitrina
   * si este parametro no es enviado traera todas las ofertas del usuario actual
   * @returns {[any]} - Objeto con los productos de la vitrina
   */
  async getOffersFromServer(offer_id: string = '0'): Promise<any> {
    console.warn('Iniciando consumo de getOffersFromServer >>>> ', offer_id);
    const options = {
      // url: this.endpointService.URLS.GET_OFFERS,
      url: this.stringFormat.transform(this.endpointService.URLS.GET_OFFERS, [
        offer_id,
      ]),
      method: 'get',
    };
    console.warn('URL  a consumir >>> ', options);
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
      /* resolve(GET_OFFERS); */
    });
  }

  /**
   * Actualiza el estado de la oferta que se visualiza en el home de los canales
   * y que es de Aprobados En Firme
   * @param {String} offer_id - Id de la oferta a actualizar
   * @param {number} status_id - Estado a asignar a la oferta de la vitrina
   * @returns {any} - Respuesta del consumo del servicio back
   */
  async updateOffer(
    status: number,
    offer_list: SpecialOfferModel[],
    product_list?: any[],
  ) {
    const offer_list2 = offer_list.map((offer: SpecialOfferModel) => {
      return { offer_id: String(offer.offerId) };
    });
    const product_list2 = product_list
      ? product_list.map(product => {
        return { product_type: product.product_code };
      })
      : undefined;
    const options = {
      url: this.endpointService.URLS.UPDATE_OFFERS,
      method: 'patch',
      body: {
        data: {
          status: String(status),
          offer_list: offer_list2,
          product_list: product_list2,
          token_id: '',
        },
      },
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
      /* resolve('Actualizado'); */
    });
  }

  setRates(ratesList): any {
    this.ratesList = ratesList;
  }

  getRates(): any {
    if (this.ratesList) {
      return this.ratesList;
    }
    return null;
  }

  /**
   * trae los productos del cliente que puede asociar con onboarding
   * envia informacion de direcciones
   *
   * *send address informatino*
   * @method get
   * @returns {Promise}
   */

  async clientProduct(): Promise<any> {
    let headers = new HttpHeaders({
      operation_target:
        this.parametersService.getParameter('headers').operation_target,
    });
    if (
      this.channelCommunicatorService.getChannelId() === CHANNEL.PORTALO.CODE
    ) {
      const { document_number, document_type } =
        this.abreCuentasService.getDocumentInfo();
      headers = headers.append(
        'token-client-id',
        `${document_number}_${document_type}`,
      );
    }
    const options: IWebRequestOptions = {
      url: this.endpointService.URLS.CLIENT_PRODUCT,
      method: 'get',
    };
    options.headers = headers;
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  async getRestrictiveList(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.RESTRICTIVE_LIST,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options) // Habilidar a true crypto
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }
  /**
   * Función de llamado de servicio de update de biocatch
   *
   * *Function that calls biocatch update service*
   *
   * @param bodyFields
   * @param csid
   * @param ppid
   * @param failedAtempts
   * @param jobDescription
   * @param yearOfBirth
   * @param ageRange
   * @param solicitationType
   * @param deviceModel
   * @param ip
   * @param maritalStatus
   * @returns {Promise}
   * @method post
   */
  biocatchUpdate(
    bodyFields: any,
    csid: string,
    ppid: string,
    failedAtempts: string,
    jobDescription: string,
    yearOfBirth: string,
    ageRange: string,
    solicitationType: string,
    deviceModel: string,
    ip: string,
    maritalStatus?: string,
  ): Promise<any> {
    const model = new ConductRequestBodyModel(
      bodyFields,
      bodyFields.operation_target,
      solicitationType,
      bodyFields.brand,
      ppid,
      bodyFields.method_description,
      deviceModel,
      ip,
    );

    model.jobDescription = jobDescription;
    model.numOfFailedAuth = failedAtempts;
    model.yob = ageRange;
    model.yearOfBirth = yearOfBirth;
    model.authMethodUsed = bodyFields.auth_method;

    if (maritalStatus) {
      model.maritalStatus = maritalStatus;
    }

    const body = {
      data: model,
    };

    const headers = new HttpHeaders({
      application: bodyFields.application,
      session_id: csid,
    });
    const options = {
      url: this.endpointService.URLS.BIOCATCH_UPDATE,
      method: 'post',
      body,
      headers,
    };
    const appendSessionHeader = csid === 'null' ? true : false;
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false, true, appendSessionHeader)
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * Funciónque llama el servicio de ppid
   *
   * *Function that calls ppid service*
   *
   * @param bodyFields
   * @returns {Promise}
   * @method get
   */
  getPpid(bodyFields: any): Promise<any> {
    const headers = new HttpHeaders({
      application: bodyFields.application,
    });
    const options = {
      url: this.endpointService.URLS.GET_PPID,
      method: 'get',
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false, true)
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  /**
   * perform para llamar los servicios de terceras fuentes desde bizagi
   *
   * *call incomes since bizagi*
   * @method post
   * @returns {Promise}
   */
  async performIncomes(body: any, solicitationId: string): Promise<any> {
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.PERFORM_INCOMES,
        [solicitationId],
      ),
      method: 'post',
      body,
    };
    console.log('--------------performIncomes------------');
    console.log(options);
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => {
          resolve(response);
        })
        .catch(err => {
          console.log('Reject >>>> ', err);
          if (this.errCrypto(err.message)) {
            this.saveBitacora('ups_error_700_crypto_incoment');
          }
          reject(err);
        });
    });
  }

  /**
   * Llamar los servicios de PCO desde bizagi
   *
   * *call PCO since bizagi*
   * @method post
   * @returns {Promise}
   */
  async performPco(body: any, solicitationId: string): Promise<any> {
    const options = {
      url: this.stringFormat.transform(this.endpointService.URLS.PERFORM_PCO, [
        solicitationId,
      ]),
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, true) // Habilidar a true crypto
        .then(response => resolve(response))
        .catch(err => {
          if (this.errCrypto(err.message)) {
            this.saveBitacora('ups_error_700_crypto_pco');
          }
          reject(err);
        });
    });
  }

  /**
   * Retorna los acuerdos de cada oferta
   *
   * *Gets Loan's Agreements*
   *
   * @method get
   * @returns {Promise}
   */
  async getAgreementsLoan(): Promise<any> {
    const options = {
      url: this.endpointService.URLS.AGREEMENTS_LOAN,
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Retorna los acuerdos de cada oferta
   *
   * *Gets Loan's Agreements*
   *
   * @method get
   * @returns {Promise}
   */
  async getAgreementsLoanDetail(id: string): Promise<any> {
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.AGREEMENTS_DETAIL,
        [id],
      ),
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }
  /*
   * Obtiene la solicitud utilizando el número de solicitud
   * @param code
   * @returns
   */
  getRequestByNumber(code?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!code) {
        reject();
        return;
      }
      resolve({
        user: {
          name: 'x',
          document: '123',
        },
        data: [
          {
            name: 'primera',
            date: '12-10-2021',
          },
        ],
      });
    });
    // const options = {
    //   url: this.stringFormat.transform(this.endpointService.URLS.REQUEST_URL, [code]),
    //   method: 'get',
    // };
    // return new Promise((resolve, reject) => {
    //   this.communicatorService
    //     .request(options, false) // Habilidar a true crypto
    //     .then(response => resolve(response))
    //     .catch(err => reject(err));
    // });
  }

  /**
   * Llamar servicio carga de documento (Prefirm)
   *
   * *call load document service (Prefirm)
   * @method post
   * @returns {Promise}
   */
  async loadDocument(body: any): Promise<any> {
    const options = {
      url: this.endpointService.URLS.LOAD_FILE,
      method: 'post',
      body,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Llamar servicio de suficiencia documental
   *
   * *call documentary sufficency service*
   * @method post
   * @returns {Promise}
   */
  async documentarySufficiency(
    principalActivityId: string,
    operationTarget: string,
  ): Promise<any> {
    principalActivityId = '36';
    operationTarget = '0001';
    const options = {
      url:
        this.endpointService.URLS.DOCUMENTARY_SUFFICIENCY +
        '?' +
        'operation_target=' +
        operationTarget +
        '&principal_activity_id=' +
        principalActivityId,
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  // async sendBackDocumentInfo2(documentInfo: DocumentModel): Promise<any> {
  //   const headers = new HttpHeaders({
  //     'channel-id': '131-2',
  //   });
  //   const options = {
  //     url: this.endpointService.URLS.SEND_BACK_DOCUMENT_INFO,
  //     method: 'post',
  //     body: documentInfo,
  //     headers,
  //   };

  //   return new Promise((resolve, reject) => {
  //     this.communicatorService
  //       .request(options, true)
  //       .then(response => {
  //         resolve(response);
  //       })
  //       .catch(err => err.status === 422 ? resolve(err) : reject(err));
  //   });
  // }

  /**
   * Verifica por documento si se encuentra en la base de relacionados
   *
   * *Checks by document if the identification is un related base*
   *
   * @method post
   * @returns {Promise}
   */
  async checkIfIsRelated(documentId: string): Promise<any> {
    //////// SE DEBE MODIFICAR LA URL Y EL BODY SEGÚN BACK
    const options = {
      url: this.endpointService.URLS.SEND_BACK_DOCUMENT_INFO,
      method: 'post',
      body: documentId,
      headerCrypto,
    };

    // return new Promise((resolve, reject) => {
    //   this.communicatorService
    //     .request(options)
    //     .then(response => {
    //       resolve(response);
    //     })
    //     .catch(err => err.status === 422 ? resolve(err) : reject(err));
    // });

    ///// Se emula en una promesa el resolve true o false (Está o no está en base relacionados)
    return new Promise(resolve => {
      resolve({
        status: 200,
        body: {
          related: false,
        },
      });
    });
    // return new Promise(reject => {
    //   reject('errrrorrrrr');
    // });
  }

  async cityBranches(cityId: string): Promise<any> {
    const options = {
      url: this.stringFormat.transform(
        this.endpointService.URLS.CITY_BRANCHES,
        [cityId],
      ),
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false)
        .then(response => resolve(response))
        .catch(err => reject(err));
    });
  }

  async districtBrances(): Promise<any> {
    // console.log('-------init getDistrict------');

    const options = {
      url: this.endpointService.URLS.BRANCHES_DISTRICT,
      method: 'get',
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false)
        .then(response => {
          resolve(response); // console.log(response);
        })
        .catch(err => {
          reject(err); // console.log(err);
        });
    });
  }
  /**
   * Valida las tasas de aliados
   *
   * *Tasas Validation*INFO_VALIDATION
   *
   * @param {any} rules
   * @returns {Promise}
   * @method post
   */
  async validateInfo(codeallies: any): Promise<any> {
    const url = this.endpointService.URLS.CONFIG_RATES_ALLY;
    const headers = new HttpHeaders({
      use_cripto: 'false',
    });
    const options = {
      url: this.stringFormat.transform(url, [codeallies]),
      method: 'get',
      headers,
    };

    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  /**
   * Trae la informacion de aliados
   *
   *
   * @param {any} rules
   * @returns {Promise}
   * @method get
   */
  async documentsAllies(): Promise<any> {
    const options = {
      url: this.endpointService.URLS.DOCUMENTS_ALLIES,
      method: 'get',
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false)
        .then(response => resolve(response.body))
        .catch(err => reject(err));
    });
  }

  saveBitacora(eventName: string) {
    const devicePrint = new LogDeviceModel();
    const product_name = this.abreCuentasService.getproductSelected()
      ? this.abreCuentasService.getproductSelected().product_code
      : '';
    devicePrint.device = this.abreCuentasService.getDeviceInfo();
    // const ipBiocatch = this.bioCatchService.getCurrentScoreIp();
    devicePrint.device.event_name = eventName;
    // devicePrint.device.ip = ipBiocatch;
    devicePrint.terms_type = eventName;
    this.saveDataInBitacora(devicePrint);
  }

  errCrypto(err) {
    let code = false;
    switch (err) {
      case '601':
        code = true;
        break;
      case 'Cannot read properties of undefined (reading "message")':
        code = true;
        break;
    }
    return code;
  }

  async getSettingsConfig(key): Promise<any> {
    const headers = new HttpHeaders({
      keyMongo: key,
    });
    const options = {
      url: this.endpointService.URLS.SETTINGS_CONFIG,
      method: 'get',
      headers,
    };
    return new Promise((resolve, reject) => {
      this.communicatorService
        .request(options, false) // Habilidar a true crypto
        .then(response => {
          resolve(response.body);
        })
        .catch(err => {
          reject(err);
        });
    });
  }
}
